import React, { useEffect, useMemo, useRef, useState } from 'react';
import { observer } from 'mobx-react-lite';
import TableContextProvider, { useTableContext, useTableControls } from 'common/components/table/table-context';
import { eiqSecureGuestInitialColumns } from 'common/list-configs/guest-list.config';
import { Breadcrumb, Button, Checkbox, Col, Form, Image, Input, Row, Select, Space, Switch } from 'antd';
import TableBase from 'common/components/table/table-base';
import eiqSecureStore from '../../stores/eiq-secure.store';
import { ReactComponent as AdmitIcon } from 'assets/icons/admit.svg';
import { ReactComponent as KeyIcon } from 'assets/icons/collect-key.svg';
import { ReactComponent as BackIcon } from 'assets/icons/back.svg';
import { ReactComponent as TurnbackIcon } from 'assets/icons/turnback.svg';
import { ReactComponent as PhoneIcon } from 'assets/icons/phone.svg';
import { FormWrapper } from '../../../../common/components/form-wrapper/form-wrapper';
import './eiq-secure-gust-license.scss';
import guestService from '../../../../common/services/guest-service/guest.service';
import { notification } from '../../../../common/utils/notification';
import { formatAddressLabel } from '../../../../common/helpers/address.helper';
import { useModal } from '../../../../common/hooks/use-modal/use-modal.hook';
import TurnbackModal, { ITurnbackModalProps } from '../turnback-modal/turnback-modal';
import ContactsModal from '../contacts-modal/contacts-modal';
import KeyActionModal from '../key-action-modal/key-action-modal-modal';
import configurationStore from 'common/stores/configuration/configuration.store';
import userAuthStore from 'common/stores/user-auth/user-auth.store';
import residentsService from '../../../../common/services/residents-service/residents.service';
import addressesKeyService from '../../../../common/services/addresses-key-service/addresses-key.service';
import InputPhoneFormItem from '../../../../common/components/form-elements/input-phone/form-item/input-phone-form-item';
import { eiqSecureGuestLicenseValidation } from './eiq-secure-guest-license.validation';
import { entryTypeMap, toGateOptions } from 'features/eiq-secure/constans/common.eiq-secure';
import SelectState from 'common/components/form-elements/select-state/select-state';
import { TurnBack, Admit } from 'features/eiq-secure/constans/admit-actions.constants';
import { useMedia } from '../../../../common/hooks/use-media/use-media.hook';
import { AdmitGuestEnum } from '../../constans/admit-types';
import Footer from '../../../../common/components/footer/footer';
import { setResidentListSearchFilter } from 'common/helpers/search-filter.helper';
import EiqSecureGuestPrintInstruction from './eiq-secure-guest-print-instruction';
import { ReactComponent as PrintIcon } from 'assets/icons/print.svg';
import { useJsApiLoader } from '@react-google-maps/api';
import { isServiceGuest } from 'common/helpers/guest-type.helpers';
import { IGuestRead } from 'common/services/residents-service/residents.service.types';
import { EntryType } from 'common/enums/entry-type.enum';
import plateRecognitionContext from 'features/eiq-secure/stores/license-plate-recognition-context';
import { useDefaultAdmitValues } from 'features/eiq-secure/hooks/use-default-admit-values/use-default-admit-values.hook';
import { useBoundPlateRecognition } from 'features/eiq-secure/hooks/use-bound-plate-recognition/use-bound-plate-recognition.hook';
import { filterColumnsByPersonalization } from 'common/components/table/table-helpers';
import { EIQ_SECURE_GUEST_LIST_TABLE_NAME } from '../eiq-secure-guests-list/eiq-secure-guest-list';
import featureStore from 'common/stores/features/feature.store';
import fastAccessService from 'common/services/fast-access-service/fast-access-service';
import citationService from 'common/services/citation-service/citation.service';
import { trackPromise } from 'react-promise-tracker';
import DriveId from '../drive-id/drive-id';
import { reaction } from 'mobx';
import { useGuestPrintInstruction } from 'features/eiq-secure/hooks/use-guest-license-print';
import { RouteLocation } from 'features/eiq-secure/services/route-for-print';
import { getIdDocumentAdmitFields, saveGuestIdDocument } from 'features/eiq-secure/heplers/id-document.helper';
import { useIdDocument } from 'features/eiq-secure/hooks/use-id-document/use-id-document.hook';
import visitorBanListService from 'common/services/visitor-ban-list-service/visitor-ban-list.service';
import AdvisoryAlertModal, { IAdvisoryAlertModalProps } from '../advisory-alert-modal/advisory-alert-modal';
import { getNotesFromCheckResult } from 'features/eiq-secure/heplers/admit-notes.helper';

interface IEiqSecureGuestLicenseProps {
  guestLicenseAdmit: boolean;
}

const LICENSE_PRINT_ID = 'license-print-id';

const openModal = (modal: ReturnType<typeof useModal>, initData: IAdvisoryAlertModalProps | ITurnbackModalProps) => {
  modal.setModalInitData(initData);
  modal.showModal();
};

export const guestEntryTypeOptions = [
  {
    label: entryTypeMap[EntryType.Driver],
    value: EntryType.Driver,
    isApproved: false,
  },
  {
    label: entryTypeMap[EntryType.Pedestrian],
    value: EntryType.Pedestrian,
    isApproved: false,
  },
  {
    label: entryTypeMap[EntryType.Passenger],
    value: EntryType.Passenger,
    isApproved: false,
  },
];

export const EiqSecureGuestLicense = observer(({ guestLicenseAdmit }: IEiqSecureGuestLicenseProps) => {
  const { isMobileOrTable } = useMedia();
  const tableContext = useTableContext();
  const [selectedAction, setSelectedAction] = useState<string>(Admit);
  const [turnbackReason, setTurnbackReason] = useState<string>();
  const [isSwitchChecked, setIsSwitchChecked] = useState(false);
  const [password, setPassword] = useState<string>('');
  const [keyOptions, setKeyOptions] = useState<{ value: string; label: string }[]>([]);
  const [isButtonsDisabled, setIsButtonsDisabled] = useState(true);
  const [selectedGate, setSelectedGate] = useState<string>();
  const [licensePlate, setLicensePlate] = useState<string>();

  const isDrivingPrivilegesRevoked = useRef(false);
  const isAdvisoryAdmit = useRef(false);
  const admitNotes = useRef<string | null>(null);

  const resetAdmissionFlags = () => {
    isDrivingPrivilegesRevoked.current = false;
    isAdvisoryAdmit.current = false;
    admitNotes.current = null;
  };

  const [form] = Form.useForm();

  const { idDocumentRef, updateFromIdDocument, onIdDocumentRecognized, onUpdateFromCameraClicked } = useIdDocument({
    form,
    shouldUseDriverName: () =>
      form.getFieldValue('entryType') === EntryType.Passenger &&
      !!configurationStore.configuration?.driveIdConfiguration?.isCaptureDriverName,
    onNamesChanged: () => handleOnValuesChange(null, form.getFieldsValue()),
    onClear: () => {
      if (eiqSecureStore.selectedGuest) {
        eiqSecureStore.setSelectedGuest({ ...eiqSecureStore.selectedGuest, idDocument: null });
      }
    },
  });
  const entryTypeValue = Form.useWatch('entryType', form);

  const isKeyPermissionAllowed = eiqSecureStore.selectedGuest?.isKeyPermissionAllowed;

  const { defaultGate, defaultLicenseState, defaultLicensePlate, image, camera } = useDefaultAdmitValues({
    currentGuest: eiqSecureStore.selectedGuest,
  });

  useBoundPlateRecognition(() => {
    const guestPlate = eiqSecureStore.selectedGuest?.carLicensePlates.find((i) => i.isPrimary);
    const fallbackState = guestPlate?.state || configurationStore.configuration?.default.defaultLicenseState;
    form.setFieldValue('carLicensePlate', guestPlate?.number || '');
    form.setFieldValue('carState', fallbackState);
    form.setFieldValue('gate', userAuthStore.defaultGate || undefined);
    const values = form.getFieldsValue();
    handleOnValuesChange(null, values);
  });

  const googleMapsApiKey = process.env.REACT_APP_GOOGLE_MAPS_API_KEY || '';
  const { isLoaded } = useJsApiLoader({ googleMapsApiKey });

  const gateOptions = useMemo(() => toGateOptions(configurationStore?.configuration?.gates || []), []);

  const defaultEntryType = useMemo(() => {
    if (plateRecognitionContext.data) {
      return EntryType.Driver;
    }

    return guestEntryTypeOptions?.find((i) => i.isApproved)?.value || guestEntryTypeOptions?.[0]?.value;
  }, []);

  const data = useMemo(() => [eiqSecureStore.selectedGuest], [eiqSecureStore.selectedGuest]);

  let columns = eiqSecureGuestInitialColumns;

  if (!featureStore.isDriveIdEnabled) {
    columns = columns.filter((i) => i.key !== 'idDocument');
  }

  // Table config
  const tableControls = useTableControls({
    clientSide: true,
    data,
    initialColumns: filterColumnsByPersonalization(columns, EIQ_SECURE_GUEST_LIST_TABLE_NAME),
  });

  useEffect(() => {
    if (isSwitchChecked) {
      const filters = `address_id: ${eiqSecureStore.selectedAddressId}`;
      addressesKeyService.getKeys({ offset: 0, limit: 20 }, filters, '').then((result) => {
        const options = result?.items?.map((item) => ({
          value: item.keyNumber.toString(),
          label: `${item.keyNumber.toString()} (${item.name})`,
          disabled: item.status === 'out',
        }));
        setKeyOptions(options);
      });
    } else {
      setKeyOptions([]);
    }
  }, [isSwitchChecked]);

  useEffect(() => {
    if (guestLicenseAdmit) {
      handleClickAdmit();
    }
  }, [guestLicenseAdmit]);

  const updateGuestAndResidentDetails = () => {
    residentsService
      .getResidentDetails(eiqSecureStore.selectedResident?.id!)
      .then((data) => {
        for (const address of data.addresses) {
          const foundGuest = address.guests.find((guest) => guest.id === eiqSecureStore.selectedGuest?.id);
          if (foundGuest) {
            eiqSecureStore.setSelectedGuest(foundGuest);
            break;
          }
        }
        tableControls.updateSelectedRow(data);
        eiqSecureStore.setResidentDetails(data);
      })
      .catch((e) => console.error(e));
  };

  const addIdDocument = async (residentId: number, addressId: number, guestId: number) => {
    const idDocument = idDocumentRef.current?.getIdDocument();
    const selectedGuest = eiqSecureStore.selectedGuest!;
    if (!idDocument) {
      return getIdDocumentAdmitFields(selectedGuest.idDocument);
    }

    const result = await saveGuestIdDocument(idDocument, residentId, addressId, guestId);
    eiqSecureStore.setSelectedGuest({ ...selectedGuest, idDocument: result });
    return getIdDocumentAdmitFields(result);
  };

  const completeGuestAction = (isAdmit: boolean) => {
    notification.success({ message: isAdmit ? 'Guest has been admitted' : 'The guest has been turned back' });
    updateGuestAndResidentDetails();
    tableContext.setSelectedRow(null);
    eiqSecureStore.setAdmitFlow(null);
    eiqSecureStore.setIsAdmitFlowProcessed();
    resetAdmissionFlags();

    const clearContext = [() => setResidentListSearchFilter(''), () => plateRecognitionContext.clear()];
    if (!isAdmit) {
      clearContext.reverse();
    }
    clearContext.forEach((clear) => clear());
  };

  const onFinish = async (values: any) => {
    const { carLicensePlate, carState, entryType } = values;
    const plateRecognitionId = plateRecognitionContext.getRecognitionId(values.gate, carLicensePlate, carState);

    const residentId = eiqSecureStore?.residentDetails?.id;
    const selectedAddressId = eiqSecureStore?.selectedAddressId;
    const selectedGuestId = eiqSecureStore?.selectedGuest?.id;

    if (!residentId || !selectedAddressId || !selectedGuestId) {
      return;
    }

    const idDocument = await addIdDocument(residentId, selectedAddressId, selectedGuestId);

    const admitData = {
      ...idDocument,
      entryType,
      carLicensePlate,
      carState,
      image,
      camera,
      driverName: entryType === EntryType.Passenger ? `${values.driverFirstName ?? ''} ${values.driverLastName ?? ''}`.trim() : null,
      driverCompanyName: entryType === EntryType.Passenger ? values.driverCompanyName : null,
      ...values,
      plateRecognitionId,
      isDrivingPrivilagesRevoked: isDrivingPrivilegesRevoked.current,
      isAdvisoryAdmit: isAdvisoryAdmit.current,
      note: admitNotes.current,
    };

    const isAdmit = selectedAction === Admit;
    if (isAdmit && password) {
      admitData.key.password = password;
    }
    if (!isAdmit) {
      admitData.reason = turnbackReason;
    }

    const saveAction = isAdmit ? 'admitGuest' : 'turnbackGuest';
    const request = guestService[saveAction](residentId, selectedAddressId, selectedGuestId, admitData).then(() => {
      completeGuestAction(isAdmit);
    });

    if (!isAdmit) {
      return request;
    }
    return request.catch((error) => {
      if (error.response.status === 403) {
        notification.error({ message: 'Password is invalid' });
      }
    });
  };

  const handleOnValuesChange = (_: any, values: any) => {
    if (!values.gate) {
      setIsButtonsDisabled(true);
      return;
    }

    if (values.entryType === EntryType.Driver && (!values.carLicensePlate || !values.carState)) {
      setIsButtonsDisabled(true);
      return;
    }

    if (isSwitchChecked && (!values.key.guestPhoneNumber || !values.key.keyNumbers?.length)) {
      setIsButtonsDisabled(true);
      return;
    }
    setSelectedGate(values.gate);
    setLicensePlate(values.carLicensePlate);
    setIsButtonsDisabled(false);
  };

  useEffect(() => {
    if (!form) {
      setIsButtonsDisabled(true);
      return;
    }
    const values = form.getFieldsValue();
    handleOnValuesChange(null, values);
  }, [form, isSwitchChecked, entryTypeValue]);

  useEffect(() => {
    const dispose = reaction(
      () => eiqSecureStore.selectedGuest?.idDocument,
      (idDocument) => {
        if (!idDocument) {
          return;
        }

        updateFromIdDocument(idDocument.firstName, idDocument.lastName);
      },
    );

    return () => {
      dispose();
      form.resetFields();
      resetAdmissionFlags();
    };
  }, []);

  const submitGuestAction = (action: string, reason?: string) => {
    setSelectedAction(action);
    if (action === TurnBack) {
      setTurnbackReason(reason);
    }
    form.submit();
  };

  const continueAdmission = async () => {
    if (featureStore.isEIQAdvisoryEnabled) {
      const result = await trackPromise(
        visitorBanListService.check({
          licensePlate: form.getFieldValue('carLicensePlate'),
          firstName: form.getFieldValue('firstName'),
          lastName: form.getFieldValue('lastName'),
          companyName: form.getFieldValue('companyName'),
        }),
      );

      isAdvisoryAdmit.current = result.hasMatch || result.hasPartialMatch;
      admitNotes.current = getNotesFromCheckResult(result);

      if (isAdvisoryAdmit.current) {
        const modalInitData: IAdvisoryAlertModalProps = {
          data: result,
          type: 'BanList',
          isVacantAddress: false,
          isShowReason: !!configurationStore.configuration?.advisory.isDisplayReasonForSecureOfficer,
        };

        openModal(visitorBanAlertModal, modalInitData);
        return;
      }
    }

    submitGuestAction(Admit);
  };

  const handleClickAdmit = async () => {
    resetAdmissionFlags();
    if (featureStore.isEIQIntelliTicketEnabled) {
      const licensePlate = form.getFieldValue('carLicensePlate');
      const result = await trackPromise(citationService.isLicensePlateDrivingPrivilegesRevoked(licensePlate));
      isDrivingPrivilegesRevoked.current = result.isDrivingPrivilegesRevoked;

      if (isDrivingPrivilegesRevoked.current) {
        const modalInitData: IAdvisoryAlertModalProps = {
          data: result,
          type: 'DrivingPrivileges',
          isVacantAddress: false,
          isShowReason: true,
        };

        openModal(drivingPrivilagesRevokeModal, modalInitData);
        return;
      }
    }

    await continueAdmission();
  };

  const drivingPrivilagesRevokeModal = useModal({
    onClose: (isSuccess, type) => {
      if (!isSuccess) {
        resetAdmissionFlags();
        return;
      }
      if (type == 'admit') {
        return continueAdmission();
      }
      if (type == 'turnback') {
        openModal(turnbackModal, { initReason: 'Driving privileges revoked' });
      }
    },
  });

  const visitorBanAlertModal = useModal({
    onClose: (isSuccess, type) => {
      if (!isSuccess) {
        resetAdmissionFlags();
        return;
      }
      if (type === Admit) {
        submitGuestAction(Admit);
      }
      if (type === TurnBack) {
        openModal(turnbackModal, { initReason: 'On banned list' });
      }
    },
  });

  const contactsModal = useModal();

  const turnbackModal = useModal({
    onClose: (isSuccessSaved, turnbackReason) => {
      if (!isSuccessSaved) {
        resetAdmissionFlags();
        return;
      }
      submitGuestAction(TurnBack, turnbackReason);
    },
  });

  const provideKeyModal = useModal({
    onClose: (isSuccessSaved, password) => {
      if (isSuccessSaved) {
        setPassword(password);
        setIsSwitchChecked(true);
      } else {
        setIsSwitchChecked(false);
        setPassword('');
      }
    },
  });

  const collectKeyModal = useModal({
    onClose: (isSuccessSaved) => {
      if (isSuccessSaved) {
        updateGuestAndResidentDetails();
      }
    },
  });

  const guest = tableControls.getSelectedRow() as IGuestRead | undefined;
  const gate = configurationStore.configuration?.gates.find((item) => item.name === selectedGate);
  const address = tableContext.getSelectedRow()?.address;
  const { city, state } = configurationStore.communitySettings ?? {};

  const hasGateCoordinates = gate?.latitude && gate?.longitude;

  const origin: RouteLocation | undefined = hasGateCoordinates
    ? {
        lat: gate.latitude!,
        lng: gate.longitude!,
      }
    : undefined;

  const destination = address && city && state ? `${state}, ${city}, ${formatAddressLabel(address)}` : undefined;

  const { printRef, route, fastAccessPassId, handlePrint } = useGuestPrintInstruction({
    isMapsLoaded: isLoaded,
    origin,
    destination,
    guestId: guest?.id,
    fastAccessEnabled: featureStore.isFastAccessPrintDirectionEnabled,
    obtainFastAccessPass: (guestId) => fastAccessService.obtainGuestFastAccessPass(guestId),
  });

  const admitBtn = (
    <Button type="primary" className="primary-btn mobile-btn" icon={<AdmitIcon />} onClick={handleClickAdmit} disabled={isButtonsDisabled}>
      Approve
    </Button>
  );

  const admitActions = (
    <Space size={8} className="buttons-font-16">
      <Button type="default" icon={<PrintIcon />} size={'middle'} onClick={handlePrint} className="mobile-btn" disabled={isButtonsDisabled}>
        Print directions
      </Button>
      <Button
        icon={<KeyIcon />}
        onClick={collectKeyModal.showModal}
        disabled={!eiqSecureStore.selectedGuest?.issuedKeys.length}
        className="mobile-btn">
        Collect key
      </Button>
      <Button className="important-btn mobile-btn" icon={<TurnbackIcon />} onClick={turnbackModal.showModal} disabled={isButtonsDisabled}>
        Turnback
      </Button>
      <Button className="secondary-btn mobile-btn" icon={<PhoneIcon />} onClick={contactsModal.showModal}>
        Dial
      </Button>
      {admitBtn}
    </Space>
  );

  const getGuestName = () => {
    const guest = tableControls.getSelectedRow() as IGuestRead;

    if (isServiceGuest(guest?.type)) {
      return guest?.companyName;
    }
    return `${guest?.firstName} ${guest?.lastName}`;
  };

  const onEntryTypeChanged = async (newValue: EntryType) => {
    if (newValue === EntryType.Passenger) {
      form.setFieldValue('carLicensePlate', '');
      form.setFieldValue('carState', defaultLicenseState);
    }
    if (newValue === EntryType.Driver) {
      form.setFieldValue('carLicensePlate', defaultLicensePlate);
      form.setFieldValue('carState', defaultLicenseState);
    }
  };

  const isInfoSectionEnabled = () => featureStore.isDriveIdEnabled || featureStore.isPlateScanEnabled;

  const onProvideKeyClicked = (checked: boolean) => {
    if (checked) {
      provideKeyModal.showModal();
    } else {
      setIsSwitchChecked(false);
      setPassword('');
    }
  };

  return (
    <div>
      <EiqSecureGuestPrintInstruction
        id={LICENSE_PRINT_ID}
        ref={printRef}
        address={tableContext.getSelectedRow()?.address}
        gate={selectedGate}
        guestLicense={licensePlate}
        guestName={getGuestName()}
        rules={configurationStore.configuration?.default.communityRules}
        communityName={configurationStore.configuration?.default.communityName ?? ''}
        routeOriginAddressString={route?.addressFrom}
        route={route}
        isMinimize={(route?.steps.length ?? 0) > 7}
        guest={tableControls.getSelectedRow()}
        fastAccessPassId={fastAccessPassId}
        speedLimit={configurationStore.configuration?.default.speedLimit}
        speedLimitImageUrl={configurationStore.configuration?.default?.speedLimitImageUrl}
        communityLogoUrl={configurationStore.configuration?.default?.communityLogoUrl}
      />
      <TableContextProvider controls={tableControls}>
        <div className="gust-license">
          <Row justify="space-between">
            <Col>
              <div className="space-12">
                <div className="breadcrumb-wrapper">
                  <Button
                    icon={<BackIcon />}
                    onClick={() => {
                      eiqSecureStore.setAdmitFlow(isMobileOrTable ? AdmitGuestEnum.SELECT_GUEST : null);
                    }}>
                    Back
                  </Button>
                  <Breadcrumb
                    separator="–"
                    items={[
                      {
                        title: ' Guests',
                        onClick: () => {
                          eiqSecureStore.setAdmitFlow(isMobileOrTable ? AdmitGuestEnum.SELECT_GUEST : null);
                        },
                      },
                    ]}
                  />
                </div>
                <span className="guest-address">{formatAddressLabel(eiqSecureStore.getSelectedAddress())}</span>
              </div>
            </Col>
            <Col>{!isMobileOrTable && admitActions}</Col>
          </Row>

          <TableBase selectFirstRowByDefault minHeight={'80px'} />

          <div className="container-row">
            <Row gutter={32}>
              {isInfoSectionEnabled() && (
                <Col lg={8} sm={24} className="instructions-wrapper">
                  {featureStore.isDriveIdEnabled && (
                    <DriveId
                      onUpdateFromCamera={onUpdateFromCameraClicked}
                      onImageRecognized={onIdDocumentRecognized}
                      ref={idDocumentRef}
                      idDocument={eiqSecureStore.selectedGuest?.idDocument}
                    />
                  )}
                  {featureStore.isPlateScanEnabled && image && (
                    <div className="instructions-item">
                      <span className="title">PlateScan ({camera})</span>
                      <Image src={image} alt="License Plate" loading="lazy" />
                    </div>
                  )}
                </Col>
              )}
              <Col lg={isInfoSectionEnabled() ? 16 : 24} xs={24}>
                <FormWrapper form={form} onFinish={onFinish} layout="vertical" onValuesChange={handleOnValuesChange}>
                  <Row gutter={{ lg: 32, md: 16, xs: 0 }}>
                    <Col lg={isInfoSectionEnabled() ? 14 : 18} xs={24}>
                      <Row gutter={21}>
                        <Col lg={isInfoSectionEnabled() ? 24 : 12} xs={24}>
                          {isServiceGuest(eiqSecureStore.selectedGuest?.type) && (
                            <Form.Item
                              name="companyName"
                              label="Company name"
                              initialValue={eiqSecureStore.selectedGuest?.companyName}
                              rules={eiqSecureGuestLicenseValidation.carState}>
                              <Input placeholder="Enter company name" />
                            </Form.Item>
                          )}
                          <Form.Item name="firstName" label="First name" initialValue={eiqSecureStore.selectedGuestFirstName}>
                            <Input placeholder="Enter first name" />
                          </Form.Item>
                          <Form.Item name="lastName" label="Last name" initialValue={eiqSecureStore.selectedGuestLastName}>
                            <Input placeholder="Enter last name" />
                          </Form.Item>
                          <Form.Item name="entryType" label="Entry Type" initialValue={defaultEntryType}>
                            <Select
                              options={guestEntryTypeOptions}
                              placeholder="Select Entry Type"
                              popupClassName="so-select"
                              onChange={onEntryTypeChanged}
                            />
                          </Form.Item>

                          {entryTypeValue === EntryType.Passenger && (
                            <>
                              <Form.Item name="driverFirstName" label="Driver first name">
                                <Input placeholder="Enter driver first name" autoFocus />
                              </Form.Item>
                              <Form.Item name="driverLastName" label="Driver last name">
                                <Input placeholder="Enter driver last name" />
                              </Form.Item>
                              <Form.Item
                                name="driverCompanyName"
                                label="Driver company name"
                                rules={eiqSecureGuestLicenseValidation.companyName}>
                                <Input placeholder="Enter driver company name" />
                              </Form.Item>
                            </>
                          )}
                        </Col>

                        <Col lg={isInfoSectionEnabled() ? 24 : 12} xs={24}>
                          {(entryTypeValue === EntryType.Driver || entryTypeValue === EntryType.Passenger) && (
                            <>
                              <Form.Item
                                name="carState"
                                label="License State"
                                initialValue={defaultLicenseState}
                                rules={eiqSecureGuestLicenseValidation.carState}>
                                <SelectState placeholder="Select a license state" popupClassName="so-select" />
                              </Form.Item>
                              <Form.Item
                                name="carLicensePlate"
                                initialValue={defaultLicensePlate}
                                label="License Plate"
                                rules={eiqSecureGuestLicenseValidation.carLicensePlate}>
                                <Input placeholder="Enter license plate" autoFocus />
                              </Form.Item>
                            </>
                          )}
                          <Form.Item
                            name="gate"
                            label="Location (Gate)"
                            rules={eiqSecureGuestLicenseValidation.gate}
                            initialValue={defaultGate}>
                            <Select options={gateOptions} placeholder="Select Location (Gate)" popupClassName="so-select" />
                          </Form.Item>
                        </Col>
                      </Row>
                    </Col>
                    {isKeyPermissionAllowed && (
                      <Col lg={isInfoSectionEnabled() ? 10 : 6} sm={24}>
                        {isKeyPermissionAllowed && (
                          <Form.Item label="Provide key">
                            <Switch onClick={onProvideKeyClicked} checked={isSwitchChecked} />
                          </Form.Item>
                        )}
                        {isSwitchChecked && (
                          <>
                            <InputPhoneFormItem
                              initPhoneNumner={eiqSecureStore.selectedGuest?.phone}
                              initCountryCode={eiqSecureStore.selectedGuest?.countryCode}
                              label="Phone number"
                              numberFieldName={['key', 'guestPhoneNumber']}
                              countryCodeFieldName={['key', 'guestPhoneCountryCode']}
                            />
                            <Form.Item name={['key', 'keyNumbers']} label="Select key" rules={eiqSecureGuestLicenseValidation.keyNumber}>
                              <Checkbox.Group options={keyOptions} className="vertical-radio-group"></Checkbox.Group>
                            </Form.Item>
                          </>
                        )}
                      </Col>
                    )}
                  </Row>
                </FormWrapper>
              </Col>
            </Row>
          </div>
        </div>
      </TableContextProvider>
      <KeyActionModal title="Collect" isOpen={collectKeyModal?.isOpenModal} onClose={collectKeyModal?.hideModal} />
      <KeyActionModal title="Provide" isOpen={provideKeyModal?.isOpenModal} onClose={provideKeyModal?.hideModal} />
      <ContactsModal title="Contacts" isOpen={contactsModal?.isOpenModal} onClose={contactsModal?.hideModal} />
      <TurnbackModal title="Turnback" isOpen={turnbackModal?.isOpenModal} onClose={turnbackModal?.hideModal} />
      <AdvisoryAlertModal
        isOpen={drivingPrivilagesRevokeModal?.isOpenModal}
        onClose={drivingPrivilagesRevokeModal?.hideModal}
        initData={drivingPrivilagesRevokeModal?.initData}
      />
      <AdvisoryAlertModal
        isOpen={visitorBanAlertModal.isOpenModal}
        onClose={visitorBanAlertModal.hideModal}
        initData={visitorBanAlertModal.initData}
      />
      {isMobileOrTable && <Footer className="guest-license-footer">{admitActions}</Footer>}
    </div>
  );
});
